import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { invitationsApi, ApiError, type InvitationDetails } from '@/lib/invitations-api'
import { trackEvent, Events } from '@/lib/observability'

export function InvitationPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const isAuthenticated = user !== null

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (token) {
      loadInvitation()
    }
  }, [token])

  const loadInvitation = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await invitationsApi.getInvitation(token!)
      setInvitation(response.invitation)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load invitation')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAccept = async () => {
    try {
      setIsProcessing(true)
      setError(null)
      const response = await invitationsApi.acceptInvitation(token!)
      trackEvent(Events.INVITATION_ACCEPTED)
      setSuccess(`You now have access to ${response.child?.firstName}'s updates!`)
      setTimeout(() => navigate('/timeline'), 2000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to accept invitation')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDecline = async () => {
    try {
      setIsProcessing(true)
      setError(null)
      await invitationsApi.declineInvitation(token!)
      trackEvent(Events.INVITATION_DECLINED)
      setSuccess('Invitation declined')
      setTimeout(() => navigate('/'), 2000)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to decline invitation')
    } finally {
      setIsProcessing(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <p className="mt-2 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Invitation Error</h1>
            <p className="mt-2 text-gray-600">{error}</p>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              Go to home
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Success!</h1>
            <p className="mt-2 text-gray-600 ph-no-capture">{success}</p>
            <p className="mt-1 text-sm text-gray-500">Redirecting...</p>
          </div>
        </div>
      </div>
    )
  }

  const emailMatches = user?.email.toLowerCase() === invitation?.parentEmail.toLowerCase()
  const isParent = user?.role === 'parent'

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900">You're Invited!</h1>
          <p className="mt-2 text-gray-600">
            You've been invited to view updates for <strong className="ph-no-capture">{invitation?.childName}</strong>
          </p>
          <p className="mt-1 text-sm text-gray-500 ph-no-capture">
            Invited by: {invitation?.childminderEmail}
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded bg-red-100 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6">
          {!isAuthenticated ? (
            <div className="space-y-3">
              <p className="text-center text-sm text-gray-600">
                Please log in or create an account to accept this invitation.
              </p>
              <p className="text-center text-xs text-gray-500">
                Use the email: <strong className="ph-no-capture">{invitation?.parentEmail}</strong>
              </p>
              <div className="flex gap-3">
                <Link
                  to={`/login?redirect=/invite/${token}`}
                  className="flex-1 rounded-lg bg-primary-600 py-2 text-center font-medium text-white hover:bg-primary-700"
                >
                  Log In
                </Link>
                <Link
                  to={`/register?redirect=/invite/${token}&email=${encodeURIComponent(invitation?.parentEmail || '')}`}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-center font-medium text-gray-700 hover:bg-gray-50"
                >
                  Register
                </Link>
              </div>
            </div>
          ) : !emailMatches ? (
            <div className="space-y-3">
              <div className="rounded bg-amber-100 p-3 text-sm text-amber-700">
                This invitation was sent to <strong className="ph-no-capture">{invitation?.parentEmail}</strong>, but you're logged in as <strong className="ph-no-capture">{user?.email}</strong>.
              </div>
              <p className="text-center text-sm text-gray-600">
                Please log in with the correct email address to accept this invitation.
              </p>
              <Link
                to={`/login?redirect=/invite/${token}`}
                className="block w-full rounded-lg bg-primary-600 py-2 text-center font-medium text-white hover:bg-primary-700"
              >
                Log In with Different Account
              </Link>
            </div>
          ) : !isParent ? (
            <div className="space-y-3">
              <div className="rounded bg-amber-100 p-3 text-sm text-amber-700">
                Only parent accounts can accept invitations. You're currently logged in as a childminder.
              </div>
              <p className="text-center text-sm text-gray-600">
                Please create a parent account with the email: <strong className="ph-no-capture">{invitation?.parentEmail}</strong>
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-center text-sm text-gray-600 ph-no-capture">
                Accept this invitation to view {invitation?.childName}'s daily updates from their childminder.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleAccept}
                  disabled={isProcessing}
                  className="flex-1 rounded-lg bg-primary-600 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {isProcessing ? 'Processing...' : 'Accept'}
                </button>
                <button
                  onClick={handleDecline}
                  disabled={isProcessing}
                  className="flex-1 rounded-lg border border-gray-300 py-2 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Decline
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">
          Expires: {invitation?.expiresAt ? new Date(invitation.expiresAt).toLocaleDateString() : 'Unknown'}
        </p>
      </div>
    </div>
  )
}
